import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { MALAY_PROGRESS_ACTIVITY_LABEL_KEY } from './teacherMalayLearningActivitiesUtils';
import {
  formatPlotStepLabel,
  formatTeacherEvidenceDate,
  groupEvidenceRecordsBySkill,
  mapSkillToTeacherEvidenceView,
  normalizeTeacherEvidenceRecord,
  resolveFormativeSkillLabel,
  type FormativeSkillRow,
  type TeacherSkillEvidenceGroup,
} from './teacherFormativeEvidencePresentation';

function levelLabel(level: string | null, t: (k: string) => string): string {
  switch (level) {
    case 'beginning':
      return t('dashboard.malayFormative.levels.beginning');
    case 'developing':
      return t('dashboard.malayFormative.levels.developing');
    case 'secure':
      return t('dashboard.malayFormative.levels.secure');
    case 'strong':
      return t('dashboard.malayFormative.levels.strong');
    default:
      return '—';
  }
}

function statusLabel(
  kind: ReturnType<typeof mapSkillToTeacherEvidenceView>['evidenceStatusKind'],
  t: (k: string) => string,
): string {
  switch (kind) {
    case 'quality':
      return t('dashboard.malayFormative.teacherEvidence.qualityEvidence');
    case 'confirmation_only':
      return t('dashboard.malayFormative.teacherEvidence.confirmationOnly');
    default:
      return t('dashboard.malayFormative.teacherEvidence.notEnoughEvidenceYet');
  }
}

function statusBadgeVariant(kind: ReturnType<typeof mapSkillToTeacherEvidenceView>['evidenceStatusKind']) {
  switch (kind) {
    case 'quality':
      return 'success' as const;
    case 'confirmation_only':
      return 'warning' as const;
    default:
      return 'gray' as const;
  }
}

function levelBadgeVariant(level: string | null) {
  switch (level) {
    case 'strong':
    case 'secure':
      return 'success' as const;
    case 'developing':
      return 'primary' as const;
    case 'beginning':
      return 'warning' as const;
    default:
      return 'gray' as const;
  }
}

function assessmentLabel(
  kind: ReturnType<typeof mapSkillToTeacherEvidenceView>['assessmentKind'],
  t: (k: string) => string,
): string | null {
  switch (kind) {
    case 'aiEvaluated':
      return t('dashboard.malayFormative.teacherEvidence.assessmentAiEvaluated');
    case 'confirmedText':
      return t('dashboard.malayFormative.teacherEvidence.assessmentConfirmedText');
    case 'confirmation':
      return t('dashboard.malayFormative.teacherEvidence.assessmentConfirmation');
    default:
      return null;
  }
}

function groupFromObservations(
  skillKey: string,
  observations?: Array<Record<string, unknown>> | null,
  evidenceGroup?: TeacherSkillEvidenceGroup | null,
): TeacherSkillEvidenceGroup | null {
  if (evidenceGroup && evidenceGroup.skillKey === skillKey) return evidenceGroup;
  const records = (observations ?? [])
    .map((raw) => normalizeTeacherEvidenceRecord(raw))
    .filter((row): row is NonNullable<typeof row> => row != null && row.skillKey === skillKey);
  return groupEvidenceRecordsBySkill(records)[0] ?? null;
}

export function FormativeEvidenceSkillCard({
  skill,
  activityKey,
  observations,
  evidenceGroup,
  testId,
}: {
  skill: FormativeSkillRow;
  activityKey?: string;
  observations?: Array<Record<string, unknown>> | null;
  evidenceGroup?: TeacherSkillEvidenceGroup | null;
  testId?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const view = mapSkillToTeacherEvidenceView(skill, observations);
  const group = groupFromObservations(skill.skill_key, observations, evidenceGroup);
  const label = resolveFormativeSkillLabel(skill.skill_key, t);
  const levelText = view.displayLevel ? levelLabel(view.displayLevel, t) : '—';
  const activityLabel =
    activityKey && activityKey in MALAY_PROGRESS_ACTIVITY_LABEL_KEY
      ? t(MALAY_PROGRESS_ACTIVITY_LABEL_KEY[activityKey as keyof typeof MALAY_PROGRESS_ACTIVITY_LABEL_KEY])
      : null;
  const latestIso = group?.latestAt ?? view.latestEvidenceAt;
  const latestDate = formatTeacherEvidenceDate(latestIso, i18n.language);
  const assessment = assessmentLabel(view.assessmentKind, t);
  const recordCount =
    view.observationCount > 0 ? view.observationCount : group?.recordCount ?? 0;
  const showCount = recordCount > 0 && (view.showObservationCount || (group?.recordCount ?? 0) > 0);
  const isConfirmationOnly = view.isConfirmationOnly;
  const countLabel = isConfirmationOnly
    ? t('dashboard.malayFormative.teacherEvidence.observationCount', { count: recordCount })
    : t('dashboard.malayFormative.teacherEvidence.evidenceRecordCount', { count: recordCount });
  const latestLabel = isConfirmationOnly
    ? t('dashboard.malayFormative.teacherEvidence.latestObservation')
    : t('dashboard.malayFormative.teacherEvidence.latestEvidence');
  const openButtonLabel = isConfirmationOnly
    ? t('dashboard.malayFormative.teacherEvidence.viewAllObservations')
    : t('dashboard.malayFormative.teacherEvidence.viewEvidenceDetails');
  const closeButtonLabel = isConfirmationOnly
    ? t('dashboard.malayFormative.teacherEvidence.hideAllObservations')
    : t('dashboard.malayFormative.teacherEvidence.hideEvidenceDetails');

  return (
    <li
      className="rounded-xl border border-slate-200/90 dark:border-slate-700/90 bg-white/80 dark:bg-slate-900/40 px-3 py-2.5"
      data-testid={testId ?? `formative-skill-card-${skill.skill_key}`}
    >
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 dark:text-white text-sm leading-snug">{label}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span data-testid="formative-skill-level">
            <Badge size="sm" variant={levelBadgeVariant(view.displayLevel)}>
              {levelText}
              {view.showLevelAsterisk ? '*' : ''}
            </Badge>
          </span>
          <Badge size="sm" variant={statusBadgeVariant(view.evidenceStatusKind)}>
            {statusLabel(view.evidenceStatusKind, t)}
          </Badge>
        </div>
        {showCount ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{countLabel}</p>
        ) : null}
      </div>

      {isConfirmationOnly ? (
        <p className="text-xs text-amber-800 dark:text-amber-200 mt-2 leading-relaxed">
          {t('dashboard.malayFormative.teacherEvidence.confirmationOnlyExplanation')}
        </p>
      ) : null}

      {group && group.plotSteps.length > 0 ? (
        <div className="mt-2" data-testid="formative-skill-plot-steps">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {t('dashboard.malayFormative.teacherEvidence.plotStepsObserved')}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
            {group.plotSteps.map((step) => (
              <li key={step}>• {formatPlotStepLabel(step)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {latestDate ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
          <span className="font-medium text-slate-600 dark:text-slate-300">{latestLabel}:</span> {latestDate}
        </p>
      ) : null}

      {!isConfirmationOnly && assessment ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{assessment}</p>
      ) : null}

      {recordCount > 0 || isConfirmationOnly ? (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-app-primary hover:underline"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? closeButtonLabel : openButtonLabel}
          {open ? <ChevronUp className="w-3.5 h-3.5" aria-hidden /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden />}
        </button>
      ) : null}

      {open ? (
        <div
          className="mt-2 space-y-2 text-xs text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-800 pt-2"
          data-testid="formative-skill-details"
        >
          {!isConfirmationOnly ? (
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">{t('dashboard.malayFormative.teacherEvidence.evidenceStatus')}</dt>
                <dd className="font-medium text-right">{statusLabel(view.evidenceStatusKind, t)}</dd>
              </div>
              {activityLabel ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">{t('dashboard.malayFormative.teacherEvidence.learningActivity')}</dt>
                  <dd className="font-medium text-right">{activityLabel}</dd>
                </div>
              ) : null}
              {recordCount > 0 ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">{t('dashboard.malayFormative.teacherEvidence.observed')}</dt>
                  <dd className="font-medium text-right">
                    {t('dashboard.malayFormative.teacherEvidence.observedTimes', {
                      count: recordCount,
                    })}
                  </dd>
                </div>
              ) : null}
              {latestDate ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">{latestLabel}</dt>
                  <dd className="font-medium text-right">{latestDate}</dd>
                </div>
              ) : null}
              {assessment ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">{t('dashboard.malayFormative.teacherEvidence.assessment')}</dt>
                  <dd className="font-medium text-right">{assessment}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}

          {group && group.records.length > 0 ? (
            <ul className="space-y-1.5" data-testid="formative-skill-evidence-records">
              {group.records.map((record, idx) => {
                const recordDate = formatTeacherEvidenceDate(record.createdAt, i18n.language);
                return (
                  <li
                    key={`${record.evidenceTurnId || record.createdAt || 'row'}-${idx}`}
                    className="rounded-md bg-slate-50/80 dark:bg-slate-800/40 px-2 py-1.5"
                  >
                    <p className="font-medium text-slate-700 dark:text-slate-200">
                      {[
                        record.plotStep ? formatPlotStepLabel(record.plotStep) : null,
                        record.masteryLevel ? levelLabel(record.masteryLevel, t) : null,
                        recordDate,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {record.excerpt ? (
                      <p className="mt-0.5 leading-relaxed text-slate-500 dark:text-slate-400">
                        {record.excerpt}
                      </p>
                    ) : null}
                    {record.reason && record.reason !== record.excerpt ? (
                      <p className="mt-0.5 leading-relaxed text-slate-500 dark:text-slate-400">
                        {record.reason}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function FormativeSkillCardsGrid({
  skills,
  activityKey,
  observations,
  evidenceBySkill,
  testId,
}: {
  skills: FormativeSkillRow[];
  activityKey?: string;
  observations?: Array<Record<string, unknown>> | null;
  evidenceBySkill?: Record<string, TeacherSkillEvidenceGroup>;
  testId?: string;
}) {
  if (skills.length === 0) return null;
  return (
    <ul
      className="grid grid-cols-1 sm:grid-cols-2 gap-2"
      data-testid={testId ?? 'formative-skill-cards'}
    >
      {skills.map((skill) => (
        <FormativeEvidenceSkillCard
          key={skill.skill_key}
          skill={skill}
          activityKey={activityKey}
          observations={observations}
          evidenceGroup={evidenceBySkill?.[skill.skill_key]}
        />
      ))}
    </ul>
  );
}

export function FormativeLevelOverview({
  overview,
}: {
  overview: {
    strong: number;
    secure: number;
    developing: number;
    beginning: number;
    confirmationOnly: number;
    notEnoughEvidence: number;
  };
}) {
  const { t } = useTranslation();
  const items = [
    overview.strong > 0
      ? { key: 'strong', label: t('dashboard.malayFormative.levels.strong'), value: overview.strong }
      : null,
    overview.secure > 0
      ? { key: 'secure', label: t('dashboard.malayFormative.levels.secure'), value: overview.secure }
      : null,
    overview.developing > 0
      ? { key: 'developing', label: t('dashboard.malayFormative.levels.developing'), value: overview.developing }
      : null,
    overview.beginning > 0
      ? { key: 'beginning', label: t('dashboard.malayFormative.levels.beginning'), value: overview.beginning }
      : null,
    overview.confirmationOnly > 0
      ? {
          key: 'confirmationOnly',
          label: t('dashboard.malayFormative.teacherEvidence.confirmationOnly'),
          value: overview.confirmationOnly,
        }
      : null,
    overview.notEnoughEvidence > 0
      ? {
          key: 'notEnough',
          label: t('dashboard.malayFormative.levels.notEnoughEvidence'),
          value: overview.notEnoughEvidence,
        }
      : null,
  ].filter((item): item is { key: string; label: string; value: number } => item != null);

  if (items.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <ul
        className="grid grid-cols-2 sm:grid-cols-4 gap-2"
        data-testid="formative-level-overview"
      >
        {items.map((item) => (
          <li
            key={item.key}
            className="rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/30 px-2.5 py-2"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
            <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums mt-0.5">{item.value}</p>
          </li>
        ))}
      </ul>
      {overview.confirmationOnly > 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('dashboard.malayFormative.teacherEvidence.overviewAssessedOnly')}
        </p>
      ) : null}
    </div>
  );
}
